import React, { useEffect, useState } from "react";
import {
  DateField,
  SimpleShowLayout,
  TextField,
  useRecordContext,
} from "react-admin";
import {
  Button,
  Chip,
  CircularProgress,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField as MuiTextField,
} from "@material-ui/core";
import { EmptyValues } from "../../../components/EmptyValues";
import { useTokenOps } from "../../../components/TokenContext";
import { hostname } from "../../../providers/api";
import ProjectVersionsSection from "./ProjectVersionsSection";

// Filter option lists (from the /activity contract).
const ACTION_OPTIONS = ["UPLOAD", "CALCULATE", "DOWNLOAD", "COMPLETENESS"];
const STATUS_OPTIONS = [
  "SUCCESS",
  "FAILED",
  "COMPLETED",
  "RUNNING",
  "CALCULATING",
  "QUEUED",
  "PROCESSING",
  "UPLOADED",
];

// Chip colors so rows can be scanned by action / status at a glance.
const ACTION_COLORS = {
  UPLOAD: "#1976d2",
  CALCULATE: "#7b1fa2",
  DOWNLOAD: "#00838f",
  COMPLETENESS: "#ed6c02",
};
const STATUS_COLORS = {
  SUCCESS: "#2e7d32",
  COMPLETED: "#2e7d32",
  UPLOADED: "#2e7d32",
  FAILED: "#d32f2f",
  RUNNING: "#1976d2",
  CALCULATING: "#1976d2",
  PROCESSING: "#ed6c02",
  QUEUED: "#757575",
};

const ColorChip = ({ value, color }) =>
  value ? (
    <Chip
      size="small"
      label={value}
      style={{ backgroundColor: color || "#9e9e9e", color: "#fff" }}
    />
  ) : (
    ""
  );

// Columns of the activity table. Maps the /activity/project/:id item fields to
// the product-defined headers. `render` is optional (defaults to raw value).
const ACTIVITY_COLUMNS = [
  { key: "startedAt", label: "Fecha", render: (v) => (v ? new Date(v).toLocaleString() : "") },
  { key: "action", label: "Acción", render: (v) => <ColorChip value={v} color={ACTION_COLORS[v]} /> },
  { key: "type", label: "Type" },
  { key: "status", label: "Status", render: (v) => <ColorChip value={v} color={STATUS_COLORS[v]} /> },
  { key: "who", label: "Usuario" },
  { key: "filename", label: "File name" },
  {
    key: "durationMs",
    label: "Duración",
    render: (v) => (v != null ? `${(v / 1000).toFixed(1)}s` : ""),
  },
];

const EMPTY_FILTERS = { action: "", status: "", type: "", dateFrom: "", dateTo: "" };

// Keys rendered as formatted dates / duration in the detail drawer.
const DETAIL_DATE_KEYS = ["uploadDate", "lastStatusDate", "runTimestamp", "createdAt", "finishedAt"];
const DETAIL_HIDE_KEYS = ["source", "id", "stacktrace", "warnings", "error", "summary"];
const fmtDetailValue = (key, v) => {
  if (v == null || v === "") return "—";
  if (DETAIL_DATE_KEYS.includes(key)) return new Date(v).toLocaleString();
  if (key === "timeTakenMs") return `${(Number(v) / 1000).toFixed(1)}s`;
  return String(v);
};

// Side drawer with the full detail of one activity row (fetched from
// GET /activity/project/:id/detail). Shows scalar fields as a list, plus dedicated blocks for
// stacktrace/error (why an upload/calc failed), warnings, and the completeness job summary.
const ActivityDetailDrawer = ({ detail, onClose }) => {
  const open = !!detail;
  const d = detail?.data;
  const block = (label, text, color) => (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color, marginBottom: 4 }}>{label}</div>
      <pre
        style={{
          margin: 0,
          padding: 10,
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 6,
          fontSize: 12,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          maxHeight: 320,
          overflowY: "auto",
        }}
      >
        {text}
      </pre>
    </div>
  );
  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <div style={{ width: "min(560px, 92vw)", padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>{detail?.row?.action} · detalle</h3>
          <Button size="small" onClick={onClose}>Cerrar</Button>
        </div>
        {detail?.loading && <CircularProgress size={24} />}
        {detail?.error && <p style={{ color: "#d32f2f" }}>Error: {detail.error}</p>}
        {d && (
          <>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <tbody>
                {Object.keys(d)
                  .filter((k) => !DETAIL_HIDE_KEYS.includes(k))
                  .map((k) => (
                    <tr key={k}>
                      <td style={{ padding: "4px 8px 4px 0", color: "#64748b", verticalAlign: "top", whiteSpace: "nowrap" }}>{k}</td>
                      <td style={{ padding: "4px 0", wordBreak: "break-word" }}>{fmtDetailValue(k, d[k])}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {d.stacktrace && block("Error / stacktrace", d.stacktrace, "#d32f2f")}
            {d.error && block("Error", d.error, "#d32f2f")}
            {d.warnings && block("Warnings", d.warnings, "#ed6c02")}
            {d.summary && block("Resultado (summary)", JSON.stringify(d.summary, null, 2), "#1976d2")}
          </>
        )}
      </div>
    </Drawer>
  );
};

// Summary tab: current project info on the left, activity table on the right.
const SummaryTab = ({ bcomeVersionRecordMap = {}, pendingBcomeVersionId }) => {
  const record = useRecordContext();
  const tokenOps = useTokenOps();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1); // 1-based, as the API expects
  const [limit, setLimit] = useState(25);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [typeInput, setTypeInput] = useState(""); // applied to filters on Enter/blur
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null); // { row, data, loading, error } — activity detail drawer

  // Click-through: fetch the full row detail from its source table.
  const openDetail = async (row) => {
    if (!row?.id || !row?.source) return;
    setDetail({ row, data: null, loading: true, error: null });
    try {
      const res = await fetch(
        `${hostname}/activity/project/${record.id}/detail?source=${encodeURIComponent(row.source)}&id=${encodeURIComponent(row.id)}`,
        { headers: { authorization: "Bearer " + tokenOps.account?.accessToken } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setDetail({ row, data, loading: false, error: null });
    } catch (err) {
      setDetail({ row, data: null, loading: false, error: err.message });
    }
  };

  // Any filter change resets to page 1 so the user isn't left on an out-of-range page.
  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const applyTypeFilter = () => {
    const trimmed = typeInput.trim();
    if (trimmed !== filters.type) updateFilter("type", trimmed);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setTypeInput("");
    setPage(1);
  };

  useEffect(() => {
    if (!record?.id) return;
    let cancelled = false;

    setLoading(true);
    setError(null);
    const query = new URLSearchParams({ page: String(page), limit: String(limit) });
    // Only send filters that are set — the API treats absent params as "no filter".
    Object.entries(filters).forEach(([key, value]) => {
      if (value) query.append(key, value);
    });

    fetch(`${hostname}/activity/project/${record.id}?${query.toString()}`, {
      headers: {
        authorization: "Bearer " + tokenOps.account?.accessToken,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
        setTotal(Number(data?.total) || 0);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id, page, limit, filters]);

  if (!record) return null;

  return (
    <>
    {/* Versions table at full width: its columns do not fit the left column. */}
    <ProjectVersionsSection
      record={record}
      versionSets={bcomeVersionRecordMap}
      accessToken={tokenOps.account?.accessToken}
      pendingBcomeVersionId={pendingBcomeVersionId}
    />
    <div
      style={{
        display: "flex",
        gap: "48px",
        alignItems: "flex-start",
        flexWrap: "wrap",
      }}
    >
      {/* Left column: the existing project summary info.
          minWidth:0 + overflowWrap let long values (URLs) wrap instead of
          spilling over the table on the right. */}
      <div
        style={{
          flex: "0 1 320px",
          minWidth: 0,
          maxWidth: 360,
          overflowWrap: "break-word",
          wordBreak: "break-word",
        }}
      >
        <SimpleShowLayout record={record}>
          <TextField source="dataCollectionURL" label="Data collection url" />
          <TextField source="folderURL" label="Folder url" />
          <TextField source="status" label="Status" />
          <DateField source="closingDate" />

          <TextField source="referenceType" label="References type" />
          <TextField source="functionalUnit" label="Functional unit" />
          <DateField showTime source="createdAt" label="Creation date" />
          <DateField showTime source="updatedAt" label="Update date" />
        </SimpleShowLayout>
      </div>

      {/* Right column: activity table from GET /activity/project/:id */}
      <div style={{ flex: "3 1 560px", minWidth: 0, paddingTop: 16 }}>
        <h3 style={{ marginBottom: 12 }}>Activity</h3>

        {/* Filters */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "flex-end",
            marginBottom: 16,
          }}
        >
          <FormControl style={{ minWidth: 140 }}>
            <InputLabel>Acción</InputLabel>
            <Select
              value={filters.action}
              onChange={(e) => updateFilter("action", e.target.value)}
            >
              <MenuItem value="">
                <em>Todas</em>
              </MenuItem>
              {ACTION_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl style={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => updateFilter("status", e.target.value)}
            >
              <MenuItem value="">
                <em>Todos</em>
              </MenuItem>
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <MuiTextField
            label="Type"
            placeholder="Match exacto"
            value={typeInput}
            onChange={(e) => setTypeInput(e.target.value)}
            onBlur={applyTypeFilter}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyTypeFilter();
            }}
            style={{ minWidth: 160 }}
          />

          <MuiTextField
            label="Desde"
            type="date"
            value={filters.dateFrom}
            onChange={(e) => updateFilter("dateFrom", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <MuiTextField
            label="Hasta"
            type="date"
            value={filters.dateTo}
            onChange={(e) => updateFilter("dateTo", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <Button size="small" onClick={clearFilters}>
            Limpiar
          </Button>
        </div>

        {loading && <CircularProgress size={24} />}
        {error && !loading && (
          <p style={{ color: "#d32f2f" }}>Error loading activity: {error}</p>
        )}
        {!loading && !error && items.length === 0 && <EmptyValues />}
        {!loading && !error && items.length > 0 && (
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {ACTIVITY_COLUMNS.map((col) => (
                    <TableCell key={col.key}>{col.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row, i) => (
                  <TableRow key={i} hover style={{ cursor: "pointer" }} onClick={() => openDetail(row)}>
                    {ACTIVITY_COLUMNS.map((col) => (
                      <TableCell key={col.key}>
                        {col.render ? col.render(row[col.key]) : (row[col.key] ?? "")}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page - 1} // TablePagination is 0-based; the API is 1-based
              onChangePage={(_e, newPage) => setPage(newPage + 1)}
              rowsPerPage={limit}
              rowsPerPageOptions={[10, 25, 50, 100]}
              onChangeRowsPerPage={(e) => {
                setLimit(parseInt(e.target.value, 10));
                setPage(1);
              }}
            />
          </Paper>
        )}
      </div>

      <ActivityDetailDrawer detail={detail} onClose={() => setDetail(null)} />
    </div>
    </>
  );
};

export default SummaryTab;
